import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Loader2, Presentation, FileText, ChevronDown, ChevronRight,
        Image, Award, Briefcase, Target, Truck, Calendar, Users,
        Building2, ArrowRightLeft, FilePlus2, Save, Link2 } from 'lucide-react';

import CoverSection from '../components/proposal/CoverSection';
import CoverLetterSection from '../components/proposal/CoverLetterSection';
import AdvantagesSection from '../components/proposal/AdvantagesSection';
import SelectedProjectsSection from '../components/proposal/SelectedProjectsSection';
import ProjectCasesSection from '../components/proposal/ProjectCasesSection';
import ProjectAlignmentSection from '../components/proposal/ProjectAlignmentSection';
import DeliveryApproachSection from '../components/proposal/DeliveryApproachSection';
import WorkScheduleSection from '../components/proposal/WorkScheduleSection';
import ResourcesSummarySection from '../components/proposal/ResourcesSummarySection';
import PartnerLogosManager from '../components/proposal/PartnerLogosManager';

import {
  getDefaultProposalData,
  createProposalFromQuote,
  DEFAULT_PROPOSAL_VARIABLES,
  mapQuoteToProposalVariables,
} from '../lib/proposalDefaultData';
import { fetchCompanyDefaults } from '../lib/brandConfig';
import { generateProposalPDF, generateMergedPDF } from '../lib/proposalPdfExport';
import { ExportDropdown } from '../components/shared/PageActions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Variable input field ──────────────────────────────────────────────────────
function VarField({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
        {label}
      </label>
      {type === 'textarea' ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
        />
      ) : (
        <input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
        />
      )}
    </div>
  );
}

// ── Section panel (collapsible) ───────────────────────────────────────────────
function SectionPanel({ title, icon: Icon, children, defaultOpen = false, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-3 border border-[#e5e4df] rounded-lg overflow-hidden bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#f8f7f4] transition-colors"
        style={{ borderBottom: open ? '1px solid rgba(201,168,76,0.15)' : 'none' }}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#c9a84c]" />}
          <span className="text-sm font-semibold text-[#1a1a2e]">{title}</span>
          {badge && (
            <span className="ml-2 px-1.5 py-0.5 text-xs bg-[#c9a84c]/10 text-[#c9a84c] rounded font-semibold">
              {badge}
            </span>
          )}
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-[#6b7280]" /> : <ChevronRight className="w-4 h-4 text-[#6b7280]" />}
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
}

// ── Section definitions ───────────────────────────────────────────────────────
const SECTIONS = [
  { key: 'cover',                    title: 'Cover',                icon: Presentation, defaultOpen: true },
  { key: 'cover_letter',             title: 'Cover Letter',         icon: FileText,     defaultOpen: true },
  { key: 'advantages',               title: 'Competitive Advantage',icon: Award,        defaultOpen: true },
  { key: 'selected_projects_intro',  title: 'Selected Projects',    icon: Briefcase,    defaultOpen: true },
  { key: 'project_cases',            title: 'Project Cases',        icon: Image,        defaultOpen: true },
  { key: 'project_alignment',        title: 'Project Alignment',    icon: Target,       defaultOpen: true },
  { key: 'delivery_approach',        title: 'Delivery Approach',    icon: Truck,        defaultOpen: true },
  { key: 'work_schedule',            title: 'Work Schedule',        icon: Calendar,     defaultOpen: true },
  { key: 'resources_summary',        title: 'Resources & Summary',  icon: Users,        defaultOpen: true },
  { key: 'partner_logos',            title: 'Partner Logos',        icon: Building2,    defaultOpen: true },
];

// ── Main Proposal Editor ──────────────────────────────────────────────────────
export default function ProposalEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // When opened from a project, return there instead of the global proposals list
  const fromProject = searchParams.get('from_project');
  const [proposalData, setProposalData] = useState(null);
  const [isLoading, setIsLoading]       = useState(true);
  const [isSaving, setIsSaving]         = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isMerging, setIsMerging]       = useState(false);
  const [lastSaved, setLastSaved]       = useState(null);
  const [isDirty, setIsDirty]           = useState(false);
  const autoSaveTimer = useRef(null);

  // ── Initialize ────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      if (id) {
        // Load existing proposal from API
        try {
          const res = await axios.get(`${API}/proposals/${id}`);
          const data = res.data;
          if (!data.variables) data.variables = { ...DEFAULT_PROPOSAL_VARIABLES };
          // Back-fill company fields that may be absent in older saved proposals
          if (!data.variables.company_website) {
            try {
              const cd = await fetchCompanyDefaults();
              if (!data.variables.company_address) data.variables.company_address = cd.company_address || '';
              if (!data.variables.company_phone)   data.variables.company_phone   = cd.company_phone   || '';
              if (!data.variables.company_director) data.variables.company_director = cd.company_director || '';
              data.variables.company_website = cd.company_website || '';
            } catch (_) {}
          }
          setProposalData(data);
          setLastSaved(data.updated_at || data.created_at);
        } catch (err) {
          console.error('Failed to load proposal:', err);
          toast.error('Proposal not found');
          navigate('/?tab=proposals');
          return;
        }
      } else {
        // New proposal
        const quoteId = searchParams.get('quote_id');
        if (quoteId) {
          try {
            const quoteRes = await axios.get(`${API}/quotes/${quoteId}`);
            const data = createProposalFromQuote(quoteRes.data);
            // Get next proposal number from API
            const numRes = await axios.get(`${API}/proposals/next-number`);
            data.proposal_no = numRes.data.proposal_no;
            data.variables.proposal_date = new Date().toISOString().slice(0, 10);
            data.client_id = quoteRes.data.client_id || '';
            setProposalData(data);
          } catch (err) {
            console.error('Failed to load quote for proposal:', err);
            const data = getDefaultProposalData();
            const numRes = await axios.get(`${API}/proposals/next-number`);
            data.proposal_no = numRes.data.proposal_no;
            data.variables.proposal_date = new Date().toISOString().slice(0, 10);
            // S-02: Fill company defaults from backend
            try {
              const cd = await fetchCompanyDefaults();
              data.variables.company_address = cd.company_address || '';
              data.variables.company_phone = cd.company_phone || '';
              data.variables.company_director = cd.company_director || '';
              data.variables.company_website = cd.company_website || '';
            } catch (_) {}
            setProposalData(data);
          }
        } else {
          const data = getDefaultProposalData();
          try {
            const numRes = await axios.get(`${API}/proposals/next-number`);
            data.proposal_no = numRes.data.proposal_no;
          } catch (_) {}
          data.variables.proposal_date = new Date().toISOString().slice(0, 10);
          // S-02: Fill company defaults from backend
          try {
            const cd = await fetchCompanyDefaults();
            data.variables.company_address = cd.company_address || '';
            data.variables.company_phone = cd.company_phone || '';
            data.variables.company_director = cd.company_director || '';
            data.variables.company_website = cd.company_website || '';
          } catch (_) {}
          setProposalData(data);
        }
      }
      setIsLoading(false);
    };
    init();
  }, [id, navigate, searchParams]);

  // ── Auto-save (debounced 3s) ──────────────────────────────────────────────
  useEffect(() => {
    if (!proposalData || !isDirty) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      handleSave(true); // silent save
    }, 3000);
    return () => clearTimeout(autoSaveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalData, isDirty]);

  // ── Save to API ───────────────────────────────────────────────────────────
  const handleSave = useCallback(async (silent = false) => {
    if (!proposalData) return;
    setIsSaving(true);
    try {
      if (id) {
        // Update existing
        const res = await axios.put(`${API}/proposals/${id}`, proposalData);
        setProposalData(res.data);
        setLastSaved(res.data.updated_at);
      } else {
        // Create new — carry the project link when started from a project
        const res = await axios.post(`${API}/proposals`, {
          ...proposalData,
          project_id: proposalData.project_id || fromProject || '',
        });
        setProposalData(res.data);
        setLastSaved(res.data.updated_at);
        // Replace URL to /proposal/:id, preserving the return path
        navigate(
          fromProject ? `/proposal/${res.data.id}?from_project=${fromProject}` : `/proposal/${res.data.id}`,
          { replace: true }
        );
      }
      setIsDirty(false);
      if (!silent) toast.success(id ? 'Proposal updated' : 'Proposal saved');
    } catch (err) {
      console.error('Save failed:', err);
      if (!silent) toast.error('Failed to save proposal');
    } finally {
      setIsSaving(false);
    }
  }, [proposalData, id, navigate, fromProject]);

  // ── Update helpers ────────────────────────────────────────────────────────
  const updateVariables = useCallback((key, value) => {
    setProposalData(prev => {
      if (!prev) return prev;
      return { ...prev, variables: { ...prev.variables, [key]: value } };
    });
    setIsDirty(true);
  }, []);

  const updateSection = useCallback((sectionKey, value) => {
    setProposalData(prev => {
      if (!prev) return prev;
      return { ...prev, [sectionKey]: value };
    });
    setIsDirty(true);
  }, []);

  // ── Pull from Quote (sync) ────────────────────────────────────────────────
  const handleSyncFromQuote = useCallback(async () => {
    if (!proposalData?.quote_id) {
      toast.error('No linked quote');
      return;
    }
    try {
      const res = await axios.put(`${API}/proposals/${id || proposalData.id}/sync-from-quote`);
      setProposalData(res.data);
      setIsDirty(false);
      toast.success('Synced from quote');
    } catch (err) {
      console.error('Sync failed:', err);
      toast.error('Failed to sync from quote');
    }
  }, [proposalData, id]);

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleGeneratePDF = useCallback(async (mode = 'download') => {
    if (!proposalData) return;
    if (isDirty) await handleSave(true);
    setIsGenerating(true);
    try {
      await generateProposalPDF(proposalData, mode);
      if (mode === 'download') toast.success('Proposal PDF generated');
    } catch (err) {
      console.error('PDF generation failed:', err);
      toast.error('Failed to generate PDF');
    } finally {
      setIsGenerating(false);
    }
  }, [proposalData, isDirty, handleSave]);

  const handlePdfPreview = () => handleGeneratePDF('preview');
  const handlePdfDownload = () => handleGeneratePDF('download');
  const handlePrint = () => window.print();

  // ── Generate Merged PDF (Proposal + Quote) ────────────────────────────────
  const handleGenerateMergedPDF = useCallback(async () => {
    if (!proposalData) return;
    const quoteId = proposalData.quote_id;
    if (!quoteId) {
      toast.error('No linked quote — link a quote to generate a merged PDF');
      return;
    }
    // Auto-save first
    if (isDirty) await handleSave(true);
    setIsMerging(true);
    try {
      const res = await axios.get(`${API}/quotes/${quoteId}`);
      await generateMergedPDF(proposalData, res.data);
      toast.success('Merged PDF (Proposal + Quote) generated');
    } catch (err) {
      console.error('Merged PDF failed:', err);
      toast.error('Failed to generate merged PDF');
    } finally {
      setIsMerging(false);
    }
  }, [proposalData, isDirty, handleSave]);

  // ── Badge counts ──────────────────────────────────────────────────────────
  const getBadge = (key) => {
    if (!proposalData) return null;
    switch (key) {
      case 'advantages':        return (proposalData.advantages || []).length || null;
      case 'project_cases':     return (proposalData.project_cases || []).length || null;
      case 'partner_logos':     return (proposalData.partner_logos || []).length || null;
      case 'delivery_approach': return (proposalData.delivery_approach?.steps || []).length || null;
      case 'work_schedule':     return (proposalData.work_schedule?.milestones || []).length || null;
      case 'resources_summary': return (proposalData.resources_summary?.team || []).length || null;
      default: return null;
    }
  };

  // ── Render section editor by key ──────────────────────────────────────────
  const renderSectionEditor = (key) => {
    if (!proposalData) return null;
    const vars = proposalData.variables || {};
    switch (key) {
      case 'cover':
        return <CoverSection data={proposalData.cover} onChange={(v) => updateSection('cover', v)} variables={vars} />;
      case 'cover_letter':
        return <CoverLetterSection data={proposalData.cover_letter} onChange={(v) => updateSection('cover_letter', v)} variables={vars} />;
      case 'advantages':
        return <AdvantagesSection data={proposalData.advantages} onChange={(v) => updateSection('advantages', v)} />;
      case 'selected_projects_intro':
        return <SelectedProjectsSection data={proposalData.selected_projects_intro} onChange={(v) => updateSection('selected_projects_intro', v)} />;
      case 'project_cases':
        return <ProjectCasesSection data={proposalData.project_cases} onChange={(v) => updateSection('project_cases', v)} />;
      case 'project_alignment':
        return <ProjectAlignmentSection data={proposalData.project_alignment} onChange={(v) => updateSection('project_alignment', v)} />;
      case 'delivery_approach':
        return <DeliveryApproachSection data={proposalData.delivery_approach} onChange={(v) => updateSection('delivery_approach', v)} />;
      case 'work_schedule':
        return <WorkScheduleSection data={proposalData.work_schedule} onChange={(v) => updateSection('work_schedule', v)} />;
      case 'resources_summary':
        return <ResourcesSummarySection data={proposalData.resources_summary} onChange={(v) => updateSection('resources_summary', v)} />;
      case 'partner_logos':
        return <PartnerLogosManager data={proposalData.partner_logos} onChange={(v) => updateSection('partner_logos', v)} />;
      default:
        return null;
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (isLoading || !proposalData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8f7f4' }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#c9a84c]" />
      </div>
    );
  }

  const vars = proposalData.variables || {};
  const hasLinkedQuote = !!proposalData.quote_id;

  return (
    <div className="min-h-screen" style={{ background: '#f8f7f4' }}>
      <Toaster position="top-right" />

      {/* Toolbar */}
      <div
        className="toolbar sticky top-0 z-40 px-6 py-3 flex items-center justify-between shadow-sm no-print"
        style={{
          background: 'rgba(248, 247, 244, 0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(201, 168, 76, 0.2)',
        }}
        data-testid="proposal-toolbar"
      >
        <div className="flex items-center gap-1 sm:gap-3">
          <button
            onClick={() => navigate(fromProject ? `/projects/${fromProject}` : '/?tab=proposals')}
            className="inline-flex items-center gap-1 text-sm font-medium text-[#1a1a2e] hover:bg-[#1a1a2e]/10 px-2 py-1 rounded transition-colors"
          >
            ← <span className="hidden sm:inline">{fromProject ? 'Project' : 'Proposals'}</span>
          </button>
          <div className="w-px h-6 bg-[#c9a84c]/30" />
          {hasLinkedQuote && (
            <button
              onClick={handleSyncFromQuote}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#c9a84c] hover:bg-[#c9a84c]/10 px-2 py-1 rounded transition-colors"
              title="Pull latest data from linked quote"
            >
              <ArrowRightLeft className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Sync from Quote</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {isDirty && (
            <span className="hidden sm:inline text-xs text-[#c9a84c] font-medium">● Unsaved changes</span>
          )}
          {lastSaved && !isDirty && (
            <span className="hidden sm:inline text-xs" style={{ color: '#9ca3af' }}>
              ✓ Saved
            </span>
          )}
          {isSaving && (
            <span className="text-xs text-[#6b7280]">
              <Loader2 className="w-3 h-3 animate-spin inline mr-1" />Saving…
            </span>
          )}
          <ExportDropdown onPdfPreview={handlePdfPreview} onPdfDownload={handlePdfDownload} onPrint={handlePrint} />
          <button
            onClick={() => handleSave(false)}
            disabled={isSaving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors bg-[#1a1a2e] text-white hover:bg-[#252545] disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span className="hidden sm:inline">{id ? 'Update' : 'Save'}</span>
          </button>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <Presentation className="w-6 h-6 text-[#c9a84c]" />
            <h1
              className="text-2xl font-bold text-[#1a1a2e]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {proposalData.proposal_no || 'New Proposal'}
            </h1>
          </div>
          {proposalData.quote_no && (
            <p className="text-sm text-[#6b7280] ml-9">
              Linked to: <span className="font-mono font-semibold">{proposalData.quote_no}</span>
              <button
                onClick={() => navigate(`/quote/${proposalData.quote_id}`)}
                className="ml-2 inline-flex items-center gap-1 text-xs text-[#c9a84c] hover:underline"
              >
                <Link2 className="w-3 h-3" />
                View Quote
              </button>
              <span className="ml-2 inline-flex items-center gap-1 text-xs text-[#c9a84c]">
                <FilePlus2 className="w-3 h-3" />
                Merge PDF available
              </span>
            </p>
          )}
        </div>

        {/* Two-column layout */}
        <div className="flex gap-6">
          {/* Left: Variables panel */}
          <div className="w-80 flex-shrink-0 hidden lg:block">
            <div className="sticky top-20 bg-white border border-[#e5e4df] rounded-lg p-4 max-h-[calc(100vh-6rem)] overflow-y-auto">
              <h3
                className="text-sm font-bold text-[#1a1a2e] uppercase tracking-wider mb-4"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Variables
              </h3>

              {/* Company info */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-[#c9a84c] uppercase mb-2">Company</p>
                <VarField label="Company Name"  value={vars.company_name}     onChange={v => updateVariables('company_name', v)} />
                <VarField label="ACN"           value={vars.company_acn}      onChange={v => updateVariables('company_acn', v)} />
                <VarField label="Phone"         value={vars.company_phone}    onChange={v => updateVariables('company_phone', v)} />
                <VarField label="Director"      value={vars.company_director} onChange={v => updateVariables('company_director', v)} />
                <VarField label="Website"       value={vars.company_website}  onChange={v => updateVariables('company_website', v)} />
                <VarField label="Address"       value={vars.company_address}  onChange={v => updateVariables('company_address', v)} type="textarea" />
              </div>

              {/* Client info */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-[#c9a84c] uppercase mb-2">Client</p>
                <VarField label="Client Name"     value={vars.client_name}           onChange={v => updateVariables('client_name', v)} />
                <VarField label="Contact Person"  value={vars.client_contact_person} onChange={v => updateVariables('client_contact_person', v)} />
                <VarField label="ABN"             value={vars.client_abn}            onChange={v => updateVariables('client_abn', v)} />
                <VarField label="Address"         value={vars.client_address}        onChange={v => updateVariables('client_address', v)} type="textarea" />
              </div>

              {/* Project info */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-[#c9a84c] uppercase mb-2">Project</p>
                <VarField label="Project Name"    value={vars.project_name}      onChange={v => updateVariables('project_name', v)} />
                <VarField label="Project Address" value={vars.project_address}   onChange={v => updateVariables('project_address', v)} type="textarea" />
                <VarField label="Date"            value={vars.proposal_date}     onChange={v => updateVariables('proposal_date', v)} type="date" />
                <VarField label="Revision"        value={vars.proposal_revision} onChange={v => updateVariables('proposal_revision', v)} />
              </div>

              {/* Sync button */}
              {hasLinkedQuote && (
                <button
                  onClick={handleSyncFromQuote}
                  className="w-full mt-2 px-3 py-2 text-xs font-semibold rounded-md border border-[#c9a84c]/30 text-[#c9a84c] hover:bg-[#c9a84c]/10 transition-colors"
                >
                  <ArrowRightLeft className="w-3.5 h-3.5 inline mr-1.5" />
                  Pull from Quote
                </button>
              )}
            </div>
          </div>

          {/* Right: Sections */}
          <div className="flex-1 min-w-0">
            {SECTIONS.map((section) => (
              <SectionPanel
                key={section.key}
                title={section.title}
                icon={section.icon}
                defaultOpen={section.defaultOpen}
                badge={getBadge(section.key)}
              >
                {renderSectionEditor(section.key)}
              </SectionPanel>
            ))}
          </div>
        </div>
      </div>

      {/* Floating Merged PDF Bar */}
      {hasLinkedQuote && (
      <div
        className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 no-print"
        style={{
          background: '#1a1a2e',
          color: 'white',
          borderRadius: '9999px',
          padding: '12px 24px',
          boxShadow: '0 8px 32px rgba(26, 26, 46, 0.4)',
          border: '1px solid rgba(201, 168, 76, 0.5)',
        }}
      >
          <button
            onClick={handleGenerateMergedPDF}
            disabled={isGenerating || isMerging}
            className="flex items-center gap-2 px-5 py-2 rounded-full font-semibold text-sm transition-all"
            style={{
              background: 'transparent',
              color: (isGenerating || isMerging) ? '#8a8a9a' : 'rgba(201,168,76,0.9)',
              border: '1px solid rgba(201,168,76,0.4)',
              cursor: (isGenerating || isMerging) ? 'not-allowed' : 'pointer',
            }}
          >
            <FilePlus2 className="w-4 h-4" />
            {isMerging ? 'Merging…' : 'Proposal + Quote PDF'}
          </button>
      </div>
      )}
    </div>
  );
}
